import type {
  ApiGroupedRouteLabel,
  ApiRootRouteLabel,
  ApiRouteLabel,
  ApiRouteLabelConfig,
  ApiRouteLabelGroup,
  ApiRouteLabelGroupInfo,
} from "griptonite-api-client";

type Item = { uuid: string; name: string; orderIndex: number; isActive: boolean };
type ItemUpdate = Partial<Omit<Item, "uuid"> & { isPublic: boolean }>;

// The server sorts by `orderIndex` and then name, so duplicated indexes from
// two admins reordering at once still give a stable order.
const byOrder = (a: Item, b: Item) => a.orderIndex - b.orderIndex || a.name.localeCompare(b.name);

const mergeUpdates = <T extends Item>(items: T[], updates: Map<string, ItemUpdate>): T[] =>
  items
    .map((item) => {
      const update = updates.get(item.uuid);
      return update ? ({ ...item, ...update } as T) : item;
    })
    .sort(byOrder);

const toUpdateMap = (updates: ({ uuid: string } & ItemUpdate)[]) =>
  new Map(updates.map(({ uuid, ...update }) => [uuid, update]));

export const applyLabelUpdates = (
  config: ApiRouteLabelConfig,
  updates: ({ uuid: string } & ItemUpdate)[],
): ApiRouteLabelConfig => {
  const updateMap = toUpdateMap(updates);
  return {
    groups: config.groups.map((group) => ({
      ...group,
      labels: mergeUpdates(group.labels, updateMap),
    })),
    labels: mergeUpdates(config.labels, updateMap),
  };
};

export const applyGroupUpdates = (
  config: ApiRouteLabelConfig,
  updates: ({ uuid: string } & ItemUpdate)[],
): ApiRouteLabelConfig => ({
  ...config,
  groups: mergeUpdates(config.groups, toUpdateMap(updates)),
});

export const addGroup = (
  config: ApiRouteLabelConfig,
  group: ApiRouteLabelGroupInfo,
): ApiRouteLabelConfig => ({
  ...config,
  groups: [...config.groups, { ...group, routeCount: 0, labels: [] }].sort(byOrder),
});

export const removeGroup = (config: ApiRouteLabelConfig, uuid: string): ApiRouteLabelConfig => ({
  ...config,
  groups: config.groups.filter((group) => group.uuid !== uuid),
});

export const removeLabel = (config: ApiRouteLabelConfig, uuid: string): ApiRouteLabelConfig => ({
  groups: config.groups.map((group) => ({
    ...group,
    labels: group.labels.filter((label) => label.uuid !== uuid),
  })),
  labels: config.labels.filter((label) => label.uuid !== uuid),
});

export const addLabel = (
  config: ApiRouteLabelConfig,
  label: ApiRouteLabel,
): ApiRouteLabelConfig => {
  const { group: groupUuid, isPublic, ...rest } = label;
  // A new label can't be on any route yet.
  const treeLabel: ApiGroupedRouteLabel = { ...rest, routeCount: 0 };
  if (groupUuid) {
    return {
      ...config,
      groups: config.groups.map((group) =>
        group.uuid === groupUuid
          ? { ...group, labels: [...group.labels, treeLabel].sort(byOrder) }
          : group,
      ),
    };
  }
  const rootLabel: ApiRootRouteLabel = { ...treeLabel, isPublic: isPublic ?? false };
  return { ...config, labels: [...config.labels, rootLabel].sort(byOrder) };
};

export type ArchivedOptionSection = {
  groupName: string;
  options: ApiGroupedRouteLabel[];
};

// Splits the full config (archived items included) into what the settings page
// shows and what the archive page shows.
export const splitRouteLabelConfig = (config: ApiRouteLabelConfig | undefined) => {
  const groups = config?.groups ?? [];
  const rootLabels = config?.labels ?? [];

  const activeGroups: ApiRouteLabelGroup[] = groups
    .filter((group) => group.isActive)
    .map((group) => ({ ...group, labels: group.labels.filter((label) => label.isActive) }));
  const activeRootLabels = rootLabels.filter((label) => label.isActive);

  const archivedGroups = groups.filter((group) => !group.isActive);

  const archivedRootLabels = rootLabels.filter((label) => !label.isActive);

  // An option in an archived group can only come back with its group, so only
  // the archived options of active groups are listed here.
  const archivedOptionSections: ArchivedOptionSection[] = groups
    .filter((group) => group.isActive)
    .map((group) => ({
      groupName: group.name,
      options: group.labels.filter((label) => !label.isActive),
    }))
    .filter((section) => section.options.length > 0);

  const archivedCount =
    archivedGroups.length +
    archivedRootLabels.length +
    archivedOptionSections.reduce((count, section) => count + section.options.length, 0);

  return {
    activeGroups,
    activeRootLabels,
    archivedGroups,
    archivedRootLabels,
    archivedOptionSections,
    archivedCount,
  };
};

// What the settings page lists: groups and root labels together in one list,
// sharing one order.
export type LabelListEntry =
  | { kind: "group"; group: ApiRouteLabelGroup }
  | { kind: "label"; label: ApiRootRouteLabel };

const entryItem = (entry: LabelListEntry) => (entry.kind === "group" ? entry.group : entry.label);

export const getEntryUuid = (entry: LabelListEntry) => entryItem(entry).uuid;

export const buildLabelList = (
  groups: ApiRouteLabelGroup[],
  labels: ApiRootRouteLabel[],
): LabelListEntry[] =>
  [
    ...groups.map((group): LabelListEntry => ({ kind: "group", group })),
    ...labels.map((label): LabelListEntry => ({ kind: "label", label })),
  ].sort((a, b) => byOrder(entryItem(a), entryItem(b)));

// The index that puts a new group or root label after everything else in the
// list. The server only knows the end of its own sequence, so a new item may
// need moving to land at the end of the combined list.
export const getListEndIndex = (config: ApiRouteLabelConfig | undefined) => {
  const indexes = [
    ...(config?.groups ?? []).map((group) => group.orderIndex),
    ...(config?.labels ?? []).map((label) => label.orderIndex),
  ];
  return indexes.length === 0 ? 0 : Math.max(...indexes) + 1;
};

// Like `toOrderUpdates`, for the combined list: the groups and the root labels
// are saved separately, each with the index it now has in the list.
export const toListOrderUpdates = (orderedUuids: string[], entries: LabelListEntry[]) => {
  const entriesByUuid = new Map(entries.map((entry) => [getEntryUuid(entry), entry]));
  const groupUpdates: { uuid: string; orderIndex: number }[] = [];
  const labelUpdates: { uuid: string; orderIndex: number }[] = [];
  orderedUuids.forEach((uuid, orderIndex) => {
    const entry = entriesByUuid.get(uuid);
    if (!entry || entryItem(entry).orderIndex === orderIndex) {
      return;
    }
    (entry.kind === "group" ? groupUpdates : labelUpdates).push({ uuid, orderIndex });
  });
  return { groupUpdates, labelUpdates };
};

export type DropEdge = "before" | "after";

// Moves `sourceId` next to `targetId` in a list of ids.
export const reorderIds = (
  ids: string[],
  sourceId: string,
  targetId: string,
  edge: DropEdge,
): string[] => {
  if (sourceId === targetId) {
    return ids;
  }
  const withoutSource = ids.filter((id) => id !== sourceId);
  const targetIndex = withoutSource.indexOf(targetId);
  if (targetIndex === -1) {
    return ids;
  }
  withoutSource.splice(edge === "before" ? targetIndex : targetIndex + 1, 0, sourceId);
  return withoutSource;
};

// The `orderIndex` updates that make `orderedIds` the new order. Items that
// already have the right index are left out.
export const toOrderUpdates = (
  orderedIds: string[],
  items: { uuid: string; orderIndex: number }[],
) => {
  const current = new Map(items.map((item) => [item.uuid, item.orderIndex]));
  return orderedIds
    .map((uuid, orderIndex) => ({ uuid, orderIndex }))
    .filter(({ uuid, orderIndex }) => current.get(uuid) !== orderIndex);
};

// The labels a setter can put on a route: active, and in an active group if
// grouped.
export const getAvailableLabelUuids = (config: ApiRouteLabelConfig) => {
  const { activeGroups, activeRootLabels } = splitRouteLabelConfig(config);
  return new Set([
    ...activeRootLabels.map((label) => label.uuid),
    ...activeGroups.flatMap((group) => group.labels.map((label) => label.uuid)),
  ]);
};
